import { useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { APP_CATALOG_KEY, useAppCatalog } from "@/hooks/apps";
import { useIsAdmin } from "@/hooks/auth";
import { putFetcher, useAuthStore, type User } from "@/lib/swr";
import type { App } from "@/types/domain";

export const ArchiveAppDialog = ({ app }: { app: App }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string>();
  const me = useAuthStore((store) => store.auth?.user);
  const isAdmin = useIsAdmin();
  const { data: catalog } = useAppCatalog();
  const { mutate } = useSWRConfig();
  const { trigger, isMutating } = useSWRMutation(
    `/apps/${app.id}/archive`, putFetcher<App>,
  );
  const canManage = me?.kind !== "bot" && (
    isAdmin || app.ownerIds?.some((id) => String(id) === String(me?.id))
  );
  if (!canManage) return null;

  const restore = !!app.archived;
  const action = restore ? "Restore" : "Archive";
  const save = async () => {
    setError(undefined);
    try {
      const saved = await trigger({ archived: !restore });
      const sameApp = (a: { id: string | number }) => String(a.id) === String(saved.id);
      const replace = (apps?: App[]) => apps?.map((a) => sameApp(a) ? saved : a);
      const visible = catalog?.some(sameApp);
      await Promise.all([
        mutate(APP_CATALOG_KEY, replace, { revalidate: false }),
        mutate(["/apps/all"], replace, { revalidate: false }),
        mutate<App[]>(["/apps"], (apps) => {
          if (!apps) return apps;
          const others = apps.filter((a) => !sameApp(a));
          return !saved.archived && visible ? [...others, saved] : others;
        }, { revalidate: false }),
        mutate<App[]>(["/apps/archived"], (apps) => {
          if (!apps) return apps;
          const others = apps.filter((a) => !sameApp(a));
          return saved.archived ? [...others, saved] : others;
        }, { revalidate: false }),
        mutate<User[]>(["/users/full"], (users) => users?.map((user) => ({
          ...user,
          visibleApps: user.visibleApps?.map((a) => sameApp(a)
            ? { ...a, archived: saved.archived }
            : a),
        })), { revalidate: false }),
      ]);
      setOpen(false);
      // Exact paths cover mounted query variants without fetching unrelated data.
      await mutate((key) => Array.isArray(key) && (
        key[0] === "/apps" || key[0] === "/apps/all" || key[0] === "/apps/archived"
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action} failed`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { setOpen(next); setError(undefined); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">{action}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{action} {app.name}?</DialogTitle>
          <DialogDescription>
            {restore
              ? "Show this App in lists and selectors and enable new notifications. Earlier notifications and reminder chains will not restart."
              : "Hide this App from lists and selectors and stop its notifications and reminders. Existing data stays available through saved links. You can restore it from Archived apps."}
          </DialogDescription>
        </DialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="outline" size="sm" disabled={isMutating} onClick={() => setOpen(false)}>Cancel</Button>
          <Button size="sm" disabled={isMutating} onClick={() => void save()}>
            {isMutating ? "Saving…" : action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
